import { addDays, differenceInCalendarDays, format } from "date-fns"
import type { GanttTask, GanttDependency } from "../gantt-chart/data/types"
import { colors } from "@/design-system/colors"
import { addWorkingDays, subtractWorkingDays, shiftWorkingDays, DEFAULT_WORKING_DAYS, type WorkingDaysConfig } from "../schedule-date-engine"

// date-fns' `parseISO` deep-imports an ESM-only submodule this project's
// Jest/webpack setup doesn't resolve cleanly -- a plain "yyyy-MM-dd" string
// parses fine as local time via the Date constructor once a time component
// is added. Same workaround used throughout this Gantt feature.
export function toDate(dateStr: string): Date {
  return new Date(`${dateStr}T00:00:00`)
}

export function toDateStr(d: Date): string {
  return format(d, "yyyy-MM-dd")
}

export function daysBetween(a: Date, b: Date): number {
  return differenceInCalendarDays(b, a)
}

export function projectDateRange(tasks: GanttTask[]): { min: Date; max: Date } {
  if (tasks.length === 0) {
    const today = new Date()
    return { min: addDays(today, -4), max: addDays(today, 20) }
  }
  const starts = tasks.map((t) => toDate(t.startDate))
  const ends = tasks.map((t) => toDate(t.endDate))
  const min = addDays(starts.reduce((a, b) => (a < b ? a : b)), -4)
  const max = addDays(ends.reduce((a, b) => (a > b ? a : b)), 6)
  return { min, max }
}

// Design-system status colors (design-system/colors.ts) -- reused here
// instead of hardcoding hex per status, matching GenericTag's own
// 'not_started'/'delayed' entries (styles/GenericTag.tsx).
export const STATUS_LABEL: Record<GanttTask["status"], string> = {
  "not-started": "Not Started",
  "in-progress": "In Progress",
  completed: "Completed",
  delayed: "Delayed",
  "on-hold": "On Hold",
}

export const STATUS_DOT_COLOR: Record<GanttTask["status"], string> = {
  "not-started": colors.tagStatusDraft,
  "in-progress": colors.tagStatusInProgress,
  completed: colors.tagStatusCompleted,
  delayed: colors.tagStatusRejected,
  "on-hold": colors.tagStatusPending,
}

// GenericTag's `value` prop for each Gantt status -- lets any Gantt
// surface (drawer, List View) render the exact same badge component every
// other entity's status uses, instead of a bespoke pill.
export const STATUS_TAG_VALUE: Record<GanttTask["status"], "not_started" | "in_progress" | "completed" | "delayed" | "on_hold"> = {
  "not-started": "not_started",
  "in-progress": "in_progress",
  completed: "completed",
  delayed: "delayed",
  "on-hold": "on_hold",
}

export const DEP_TYPE_LABEL: Record<GanttDependency["type"], string> = {
  "finish-to-start": "Finish → Start",
  "start-to-start": "Start → Start",
  "finish-to-finish": "Finish → Finish",
  "start-to-finish": "Start → Finish",
}

export const PHASE_PALETTE = ["#64748b", "#b45309", "#ed5900", "#dc2626", "#2563eb", "#9333ea", "#0d9488", "#16a34a", "#334155"]

// -- Scheduling / date engine (single source of truth for Gantt + List;
// Calendar reads the same schedule_tasks rows so it inherits the
// result without its own copy of this logic) -----------------------------
//
// recomputeSchedule is a full topological recompute, not an incremental
// "push if violated" pass: every task that has one or more incoming
// dependencies gets its start/end recomputed EXACTLY from its
// predecessor(s) + dependency type + lag + company working days, every
// time this runs -- regardless of whether the predecessor moved forward
// or backward. This is what makes "move a task 40 days earlier" actually
// pull its successors back too, and what guarantees lag_days is NEVER
// altered by a move (lag is read, never written, by this function).
//
// Required start/end per dependency type (lag is WORKING days, not
// calendar days -- see schedule-date-engine.ts). lag can be positive,
// zero, or NEGATIVE (an allowed overlap, e.g. "successor may start 2
// working days before the predecessor finishes") -- shiftWorkingDays
// picks addWorkingDays vs subtractWorkingDays by the sign of lag, so a
// negative lag genuinely pulls the required date earlier instead of
// being clamped to zero:
//   finish-to-start:  successor start = (predecessor end + 1 calendar day
//                      if lag >= 0, else predecessor end itself)
//                      shifted by lag working days
//   start-to-start:   successor start = predecessor start shifted by lag working days
//   finish-to-finish: successor end   = predecessor end shifted by lag working days
//   start-to-finish:  successor end   = predecessor start shifted by lag working days
// (FS is the one type with a sign-dependent anchor: lag>=0 can't let the
// successor start ON the predecessor's own finish day, so the anchor is
// pushed one calendar day later first; a negative lag is an intentional
// overlap, so no such push applies -- the anchor is the finish day itself.)
// A task with multiple incoming dependencies takes the LATEST (max) of
// all its computed required dates -- every incoming constraint must be
// satisfied, same as standard CPM scheduling.
export function recomputeSchedule(
  tasks: GanttTask[],
  deps: GanttDependency[],
  workingDays: WorkingDaysConfig = DEFAULT_WORKING_DAYS,
  // Task ids the caller just set explicitly (drag, resize, a manual
  // Start/Duration field edit, a bulk shift) -- these are the ONE thing
  // this function must never overwrite. Without this, dragging a task
  // that itself has an incoming dependency got silently snapped back to
  // wherever its predecessor dictates on every recompute, because the
  // topological loop below doesn't otherwise distinguish "the task the
  // user just positioned" from "a stale successor that needs to catch
  // up" -- both simply have incoming deps. A pinned id still fully
  // participates in the topological order (so its successors see its
  // new position and cascade correctly); it just skips its OWN
  // recompute-from-predecessor step for this pass.
  pinnedIds: ReadonlySet<string> = new Set()
): GanttTask[] {
  const byId = new Map(tasks.map((t) => [t.id, t]))
  const incoming = new Map<string, GanttDependency[]>()
  const outgoing = new Map<string, GanttDependency[]>()
  for (const d of deps) {
    if (!byId.has(d.fromTaskId) || !byId.has(d.toTaskId)) continue
    if (!incoming.has(d.toTaskId)) incoming.set(d.toTaskId, [])
    incoming.get(d.toTaskId)!.push(d)
    if (!outgoing.has(d.fromTaskId)) outgoing.set(d.fromTaskId, [])
    outgoing.get(d.fromTaskId)!.push(d)
  }

  // Kahn's algorithm -- topological order guarantees every predecessor is
  // recomputed before its successors are processed. Any task that never
  // reaches in-degree 0 is part of a cycle (spec #7) and is left OUT of
  // `order` entirely -- it keeps its current position rather than risk an
  // infinite loop. Cycles are also rejected up front at creation time by
  // wouldCreateCycle() below; this is just the last-resort safety net.
  const inDegree = new Map<string, number>()
  for (const t of tasks) inDegree.set(t.id, (incoming.get(t.id) || []).length)
  const queue: string[] = []
  Array.from(inDegree.entries()).forEach(([id, deg]) => { if (deg === 0) queue.push(id) })
  const order: string[] = []
  while (queue.length) {
    const id = queue.shift()!
    order.push(id)
    for (const dep of outgoing.get(id) || []) {
      const next = (inDegree.get(dep.toTaskId) ?? 0) - 1
      inDegree.set(dep.toTaskId, next)
      if (next === 0) queue.push(dep.toTaskId)
    }
  }

  for (const id of order) {
    if (pinnedIds.has(id)) continue
    const depsIn = incoming.get(id)
    if (!depsIn || depsIn.length === 0) continue
    const task = byId.get(id)!

    let requiredStart: Date | null = null
    let requiredEnd: Date | null = null
    for (const dep of depsIn) {
      const from = byId.get(dep.fromTaskId)
      if (!from) continue
      const lag = dep.lagDays ?? 0
      const fromStart = toDate(from.startDate)
      const fromEnd = toDate(from.endDate)

      let candidateStart: Date | null = null
      let candidateEnd: Date | null = null
      switch (dep.type) {
        case "finish-to-start":
          // lag>=0: can't start ON the finish day, anchor is the day after.
          // lag<0: an intentional overlap, anchor is the finish day itself.
          candidateStart = shiftWorkingDays(lag >= 0 ? addDays(fromEnd, 1) : fromEnd, lag, workingDays)
          break
        case "start-to-start":
          candidateStart = shiftWorkingDays(fromStart, lag, workingDays)
          break
        case "finish-to-finish":
          candidateEnd = shiftWorkingDays(fromEnd, lag, workingDays)
          break
        case "start-to-finish":
          candidateEnd = shiftWorkingDays(fromStart, lag, workingDays)
          break
      }
      if (candidateStart && (!requiredStart || candidateStart > requiredStart)) requiredStart = candidateStart
      if (candidateEnd && (!requiredEnd || candidateEnd > requiredEnd)) requiredEnd = candidateEnd
    }

    let newStart = toDate(task.startDate)
    let newEnd = toDate(task.endDate)
    if (requiredStart) {
      newStart = requiredStart
      newEnd = addWorkingDays(newStart, task.duration, workingDays)
    } else if (requiredEnd) {
      newEnd = requiredEnd
      newStart = subtractWorkingDays(newEnd, task.duration, workingDays)
    }

    const newStartStr = toDateStr(newStart)
    const newEndStr = toDateStr(newEnd)
    if (newStartStr !== task.startDate || newEndStr !== task.endDate) {
      byId.set(id, { ...task, startDate: newStartStr, endDate: newEndStr })
    }
  }

  return Array.from(byId.values())
}

// Pre-validation before a new dependency is created (spec #7): would
// fromTaskId -> toTaskId close a cycle? True iff toTaskId can already
// reach fromTaskId through existing edges (i.e. a path toTaskId -> ... ->
// fromTaskId already exists, so adding fromTaskId -> toTaskId would close
// the loop).
export function wouldCreateCycle(deps: GanttDependency[], fromTaskId: string, toTaskId: string): boolean {
  if (fromTaskId === toTaskId) return true
  const outgoing = new Map<string, string[]>()
  for (const d of deps) {
    if (!outgoing.has(d.fromTaskId)) outgoing.set(d.fromTaskId, [])
    outgoing.get(d.fromTaskId)!.push(d.toTaskId)
  }
  const visited = new Set<string>()
  const stack = [toTaskId]
  while (stack.length) {
    const id = stack.pop()!
    if (id === fromTaskId) return true
    if (visited.has(id)) continue
    visited.add(id)
    for (const next of outgoing.get(id) || []) stack.push(next)
  }
  return false
}

// -- Schedule Health (spec #12/#4): exactly two cases, computed in ONE
// place and reused everywhere a Schedule Item renders (Gantt, List View;
// Calendar -- see calendar-view.tsx's own gantt task chips). Never
// evaluated for a Phase/group row, only real Task rows.
export type ScheduleHealthFlag = "no-progress-overdue-start" | "incomplete-overdue-end" | null

export function getScheduleHealthFlag(task: GanttTask, today: Date = new Date()): ScheduleHealthFlag {
  if (task.type !== "task") return null
  const todayStr = toDateStr(today)
  if (task.progress === 0 && task.startDate < todayStr) return "no-progress-overdue-start"
  if (task.status !== "completed" && task.endDate < todayStr) return "incomplete-overdue-end"
  return null
}

export const SCHEDULE_HEALTH_LABEL: Record<NonNullable<ScheduleHealthFlag>, string> = {
  "no-progress-overdue-start": "Not started, start date has passed",
  "incomplete-overdue-end": "Incomplete, past its end date",
}
